import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Box, IconButton, MenuItem, Stack } from '@mui/material'
import Menu, { MenuProps } from '@mui/material/Menu'
import { styled } from '@mui/material/styles'
import { useDeleteMilkerMutation } from 'hook/mutations/useDeleteMilkerMutation'
import { FC, Ref, UIEventHandler, useState } from 'react'
import { COLORS } from 'styles/colors'
import ButtonFunc from 'ui/buttons/ButtonFunc'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { getFirstWordsFromName } from 'utils'
import { ReactComponent as ManagementEditLogo } from '../../../assets/management_edit_logo.svg'
import { ReactComponent as TrashLogo } from '../../../assets/trash_logo.svg'
import { AddEmployeeDrawer } from './AddEmployeeDrawer'
import { DeleteMilkerDialog } from './DeleteMilkerDialog'

export interface ShiftEmployee {
	id: number | string
	firstname: string
	lastname: string
	avatar: string | null
}

const EMPLOYEE_ROW_HIGHLIGHT = '#5222D00D'

interface ShiftEmployeeListProps {
	employees: ShiftEmployee[]
	employeeRowHeights?: Map<number, number>
	selectedEmployeeId?: number | null
	onEmployeeClick?: (employee: ShiftEmployee) => void
	onEditClick?: (employee: ShiftEmployee) => void
	onDeleteClick?: (employee: ShiftEmployee) => void
	scrollRef?: Ref<HTMLDivElement>
	onScroll?: UIEventHandler<HTMLDivElement>
}

const StyledMenu = styled((props: MenuProps) => (
	<Menu
		elevation={0}
		anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
		transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		{...props}
	/>
))(({ theme }) => ({
	'& .MuiPaper-root': {
		borderRadius: 8,
		marginTop: theme.spacing(1.5),
		minWidth: 220,
		boxShadow: '0px 0px 16px 0px #04060629',
		overflow: 'visible',
		'&::before': {
			content: '""',
			display: 'block',
			position: 'absolute',
			top: 0,
			right: 6,
			width: 12,
			height: 12,
			backgroundColor: '#fff',
			transform: 'translateY(-50%) rotate(45deg)',
			zIndex: 0,
			boxShadow: '-2px -2px 6px rgba(4, 6, 6, 0.06)',
		},
		'& .MuiMenu-list': {
			padding: '16px 8px',
			display: 'flex',
			flexDirection: 'column',
			gap: '10px',
		},
		'& .MuiMenuItem-root': {
			gap: '8px',
			padding: '4px 10px',
			borderRadius: '4px',
			fontSize: '16px',
			fontWeight: 500,
			'&:hover': {
				backgroundColor: 'transparent',
			},
			'&:active': {
				backgroundColor: 'transparent',
			},
		},
	},
}))

const EmployeeAvatar: FC<{ employee: ShiftEmployee }> = ({ employee }) => {
	if (employee.avatar) {
		return (
			<Box
				component='img'
				src={employee.avatar}
				alt='Avatar'
				sx={{
					width: '40px',
					height: '40px',
					borderRadius: '50%',
					objectFit: 'cover',
				}}
			/>
		)
	}

	return (
		<Box
			sx={{
				width: '40px',
				height: '40px',
				borderRadius: '50%',
				background: COLORS.background,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				color: COLORS.main,
				fontWeight: 600,
				fontSize: '14px',
				flexShrink: 0,
			}}
		>
			{getFirstWordsFromName(employee.firstname, employee.lastname)}
		</Box>
	)
}

export const ShiftEmployeeList: FC<ShiftEmployeeListProps> = ({
	employees,
	employeeRowHeights,
	selectedEmployeeId = null,
	onEmployeeClick,
	onEditClick,
	onDeleteClick,
	scrollRef,
	onScroll,
}) => {
	const deleteMilker = useDeleteMilkerMutation()
	const [isDrawerOpen, setIsDrawerOpen] = useState(false)
	const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
	const [activeEmployee, setActiveEmployee] = useState<ShiftEmployee | null>(
		null,
	)

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLElement>,
		employee: ShiftEmployee,
	) => {
		setAnchorEl(event.currentTarget)
		setActiveEmployee(employee)
	}

	const handleMenuClose = () => {
		setAnchorEl(null)
	}

	return (
		<>
			<Stack
				direction='column'
				gap='10px'
				sx={{ pb: '12px', overflow: 'visible' }}
			>
				<Stack direction='column' gap='10px'>
					{employees.map((employee, index) => {
						const employeeId = Number(employee.id)
						const isSelected = selectedEmployeeId === employeeId

						return (
							<Box
								key={employee.id}
								sx={{
									minHeight: `${employeeRowHeights?.get(employeeId) ?? 70}px`,
									height: `${employeeRowHeights?.get(employeeId) ?? 70}px`,
									display: 'flex',
									alignItems: 'center',
									boxShadow:
										index < employees.length - 1
											? 'inset 0 -1px 0 #DBDBDB'
											: 'none',
								}}
							>
								<Stack
									direction='row'
									alignItems='center'
									gap='8px'
									onClick={() => onEmployeeClick?.(employee)}
									sx={{
										width: '100%',
										pr: '8px',
										pl: '4px',
										py: '6px',
										borderRadius: '8px',
										cursor: onEmployeeClick ? 'pointer' : 'default',
										backgroundColor: isSelected
											? EMPLOYEE_ROW_HIGHLIGHT
											: 'transparent',
										'&:hover': {
											backgroundColor: EMPLOYEE_ROW_HIGHLIGHT,
										},
									}}
								>
									<EmployeeAvatar employee={employee} />
									<TypographyPrimary sx={{ flex: 1, minWidth: 0 }} noWrap>
										{`${employee.lastname} ${employee.firstname}`}
									</TypographyPrimary>
									<IconButton
										size='small'
										onClick={e => {
											e.stopPropagation()
											handleMenuOpen(e, employee)
										}}
										sx={{
											color: COLORS.secondary,
											width: '24px',
											height: '24px',
											p: 0,
											flexShrink: 0,
										}}
									>
										<MoreVertIcon fontSize='small' />
									</IconButton>
								</Stack>
							</Box>
						)
					})}
				</Stack>
				<Box
					sx={{
						px: '12px',
						width: '100%',
						boxSizing: 'border-box',
						overflow: 'visible',
					}}
				>
					<ButtonFunc
						fullWidth
						title='Добавить дояра'
						onClick={() => setIsDrawerOpen(true)}
					/>
				</Box>
			</Stack>

			<StyledMenu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
			>
				<MenuItem
					disableRipple
					onClick={() => {
						handleMenuClose()
						setIsEditDrawerOpen(true)
					}}
				>
					<ManagementEditLogo />
					Редактировать
				</MenuItem>
				<MenuItem
					disableRipple
					onClick={() => {
						handleMenuClose()
						setIsDeleteDialogOpen(true)
					}}
				>
					<TrashLogo />
					Удалить
				</MenuItem>
			</StyledMenu>

			<AddEmployeeDrawer
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
			/>

			<AddEmployeeDrawer
				open={isEditDrawerOpen}
				onClose={() => {
					setIsEditDrawerOpen(false)
					setActiveEmployee(null)
				}}
				employee={
					activeEmployee
						? {
								id: activeEmployee.id,
								fullName: `${activeEmployee.lastname} ${activeEmployee.firstname}`,
								avatar: activeEmployee.avatar,
							}
						: undefined
				}
			/>

			<DeleteMilkerDialog
				open={isDeleteDialogOpen}
				employeeName={
					activeEmployee
						? `${activeEmployee.lastname} ${activeEmployee.firstname}`
						: ''
				}
				onClose={() => {
					setIsDeleteDialogOpen(false)
					setActiveEmployee(null)
				}}
				onConfirm={() => {
					if (activeEmployee) {
						deleteMilker.mutate(
							{
								id: activeEmployee.id,
								name: `${activeEmployee.lastname} ${activeEmployee.firstname}`,
							},
							{
								onSuccess: () => {
									setIsDeleteDialogOpen(false)
									setActiveEmployee(null)
								},
							},
						)
					}
				}}
			/>
		</>
	)
}
